/**
 * Genre Service
 *
 * Provides access to track genre/tag data from Last.fm and MusicBrainz providers.
 * Genre data is cached permanently in the backend database.
 */

import { getApiClient } from '../client';
import type { TrackGenres, BatchGenreRequest, BatchGenreResponse } from '../types';

export const genreService = {
  /**
   * Get genres for a single track
   *
   * Fetches genre tags from Last.fm and MusicBrainz providers, merged with
   * weight boosting. Returns top 5 genres sorted by relevance.
   *
   * @param trackId - Spotify track ID
   * @returns Track genres with provider information
   */
  async getTrackGenres(trackId: string): Promise<TrackGenres> {
    return getApiClient().get<TrackGenres>(`/genres/tracks/${encodeURIComponent(trackId)}`);
  },

  /**
   * Get genres for multiple tracks in batch
   *
   * Processes up to 50 tracks. Failed tracks are skipped silently.
   *
   * @param trackIds - Array of Spotify track IDs (max 50)
   * @returns Batch response with genres for successfully processed tracks
   */
  async getTrackGenresBatch(trackIds: string[]): Promise<BatchGenreResponse> {
    const request: BatchGenreRequest = { trackIds };
    return getApiClient().post<BatchGenreResponse>('/genres/tracks/batch', request);
  },
};
